import { css } from "@emotion/css";
import { Button, Callout, Snackbar, InlineMessage } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusMutation } from "@octopusdeploy/octopus-react-client";
import type { Repository } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import RequestFreeTrialDialog from "~/components/DialogLayout/RequestFreeTrialDialog";
import { RequestTrialLink } from "~/components/RequestTrial/RequestTrialLink";
export interface RequestTrialProps {
    type?: "callout" | "inline-message";
    featureName: string;
    onRequestTrial: (repository: Repository) => Promise<void>;
    content: string;
    dialogTitle: string;
    variant?: "link" | "button";
}
export const RequestTrial = ({ onRequestTrial: onRequestTrialProp, content, dialogTitle, featureName, variant = "link", type = "callout" }: RequestTrialProps) => {
    const [showSnackbar, setShowSnackbar] = React.useState(false);
    const { closeAllDialogs, openDialog, isOpen: isDialogOpen } = useDialogTrigger();
    const onRequestTrialLinkClicked = async () => {
        openDialog();
    };
    const { error: errorRequestingTrial, execute: requestFreeTrial, isExecuting: isRequestingFreeTrial, } = useInlineStatusMutation({
        action: onRequestTrialProp,
        name: `request free trial ${featureName}`,
        afterComplete: async () => {
            closeAllDialogs();
            setShowSnackbar(true);
        },
    });
    const renderTrigger = () => {
        if (variant === "button") {
            return <Button size="medium" importance="loud" onClick={onRequestTrialLinkClicked} label="Request Free Trial"/>;
        }
        return <RequestTrialLink onClick={onRequestTrialLinkClicked}/>;
    };
    const renderCallout = () => {
        return (<Callout type={"upsell"} hideTitle={true}>
                <div className={variant === "button" ? styles.buttonContent : styles.linkContent}>
                    <span>{content}</span>
                    {renderTrigger()}
                </div>
            </Callout>);
    };
    const renderInlineMessage = () => {
        return (<InlineMessage type="upsell" description={content} action={{
                onClick: () => onRequestTrialLinkClicked(),
                label: "Request a free trial",
            }}/>);
    };
    return (<>
            <div className={styles.calloutContainer}>
                {type === "callout" ? renderCallout() : renderInlineMessage()}
                <Dialog open={isDialogOpen}>
                    <RequestFreeTrialDialog errors={errorRequestingTrial ? [errorRequestingTrial] : undefined} busy={isRequestingFreeTrial} dialogTitle={dialogTitle} onRequestTrial={requestFreeTrial}/>
                </Dialog>
            </div>
            <Snackbar open={showSnackbar} autoHideDuration={8000} onClose={() => setShowSnackbar(false)} content={"Trial request sent. We'll email you shortly with the details."} textAlign={"center"} size={"small"}/>
        </>);
};
const styles = {
    calloutContainer: css({
        marginBottom: space[16],
    }),
    linkContent: css({
        display: "inline-flex",
        gap: space[4],
    }),
    buttonContent: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
        alignItems: "flex-start",
    }),
};
